/**
 * The note that explains why memory can look empty, above a project's Memory tab and the
 * organisation's Memory page, and the prompt that asks a project's owners to classify it.
 * Compartments and clearances themselves are on the Memory page's Access view (memoryAccess.tsx).
 */

import { CLASSIFICATION_DESCRIPTIONS, CLASSIFICATION_ORDER, type Classification, type MemoryAccess, type Sensitivity } from '@aioe/shared';
import { Button } from '@heroui/react';
import { CircleInfo, Shield } from '@gravity-ui/icons';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { problemText, routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { useMay } from '../permissions.ts';
import { LEVEL_TEXT } from './memoryAccessModel.ts';
import { ChoiceSelect, ErrorNote, Panel } from './ui.tsx';
import { VIEW_TONES, type ArtTone } from './ui/Art.tsx';
import { Explainer } from './ui/Layout.tsx';

const items = (n: number) => `${n.toLocaleString()} ${n === 1 ? 'item' : 'items'}`;
const isAre = (n: number) => (n === 1 ? 'is' : 'are');

/**
 * "Why memory can look empty", above a project's Memory tab and the organisation's Memory page, told from the
 * platform's own answer (`GET …/memory/access`). On a project: its members read up to its classification (Internal
 * while it has none), compartments still need the compartment, and items labelled above the classification need the
 * person's own clearance. On the organisation's memory: clearance alone. Only owners (on the organisation's memory,
 * its writers) hear how many items are hidden, why, and the one change that opens most of them.
 */
export function MemoryAccessNote({ projectId, admin: isAdmin, tone, onClassified }: { projectId?: string; /** Unused: the platform decides who hears the counts. */ owner?: boolean; admin?: boolean; tone?: ArtTone; onClassified?: () => void }) {
  // Raising clearances and granting compartments: Manage on memory (today: administrators), unless the page already knows.
  const manages = useMay('data.memory', 'manage');
  const admin = isAdmin ?? manages ?? false;
  const access = useLoad<MemoryAccess | undefined>(() => routes.memoryAccess(projectId).catch(() => undefined), [projectId]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const a = access.data;
  if (access.loading || !a) return null;
  const e = a.entitlement;
  const hidden = a.hidden;
  const held = e.compartments.length;
  const inProject = Boolean(projectId);
  // Nothing to explain: a classified project whose owner sees everything, or a reader cleared to the top on the organisation's memory.
  if (inProject ? a.classified && hidden && hidden.total === 0 : e.clearance === 'restricted' && !hidden?.total) return null;
  const level = a.level as Sensitivity | undefined;
  const fromProject = Boolean(e.projectGrant && e.projectGrant === e.clearance && e.organisationClearance !== e.clearance);
  const summary = `You read up to ${LEVEL_TEXT[e.clearance]}${inProject ? (fromProject ? ", the project's classification" : '') : ''}${held ? ` and hold ${held} ${held === 1 ? 'compartment' : 'compartments'}` : ''}${hidden?.total ? `; ${items(hidden.total)} ${isAre(hidden.total)} hidden from you` : ''}${inProject && !a.classified ? '; this project is not classified yet' : ''}`;
  const classify = async (to: Classification) => {
    if (!projectId) return;
    setBusy(true);
    setError(undefined);
    try {
      await routes.setProjectClassification(projectId, to);
      access.reload();
      onClassified?.();
    } catch (err) {
      setError(problemText(err));
    } finally {
      setBusy(false);
    }
  };
  const accessLink = (
    <Link to="/memory?view=access" className="text-accent hover:underline">
      Access on the Memory page
    </Link>
  );
  return (
    <Explainer id={projectId ? 'memory-empty-project' : 'memory-empty'} tone={tone ?? (projectId ? VIEW_TONES.projects! : VIEW_TONES.memory!)} icon={<CircleInfo />} title="Why memory can look empty" summary={summary}>
      {inProject ? (
        <p>
          Members of a project read its memory up to the project&apos;s classification
          {level ? (
            <>
              , <span className="font-medium text-foreground">{LEVEL_TEXT[level]}</span> here
            </>
          ) : null}
          . Anything nobody labelled takes that level. An item in a compartment needs the compartment too, and an item labelled above the classification (by a labelling rule or a person) needs your own clearance.
          {!a.classified ? ' This project has no classification yet, so its memory counts as Internal: members do not see anything labelled Confidential or Restricted until an owner sets one.' : ''}
        </p>
      ) : (
        <p>
          The organisation&apos;s memory is read by clearance: you read an item when its level is at or below your clearance and you hold every compartment on it. Anything no labelling rule covers is Confidential, and most people are cleared to Internal. A project&apos;s own memory is different: its members read it up to the project&apos;s classification.
        </p>
      )}
      <p>
        You read up to <span className="font-medium text-foreground">{LEVEL_TEXT[e.clearance]}</span>
        {inProject && e.organisationClearance && e.organisationClearance !== e.clearance ? ` (your own clearance is ${LEVEL_TEXT[e.organisationClearance]}; as a member you read to the project's classification)` : ''}
        {held ? (
          <span title={e.compartments.join(', ')}>
            {' '}
            and hold {held} {held === 1 ? 'compartment' : 'compartments'}
          </span>
        ) : (
          ' and hold no compartments'
        )}
        .{' '}
        {hidden ? (hidden.total ? hiddenWords(hidden, inProject) : 'Nothing is hidden from you. ') : inProject ? "How many items are hidden is shown to the project's owners only." : 'Items you cannot read are not counted here.'}
      </p>
      {a.fix ? (
        <div className="flex flex-wrap items-center gap-3">
          {a.fix.action === 'classify' && a.fix.level ? (
            <>
              <span>
                Classify the project {LEVEL_TEXT[a.fix.level]} and its members read {items(a.fix.unlocks)} more. {CLASSIFICATION_DESCRIPTIONS[a.fix.level as Classification]}
              </span>
              <Button size="sm" variant="secondary" isDisabled={busy} onPress={() => void classify(a.fix!.level as Classification)}>
                Classify as {LEVEL_TEXT[a.fix.level]}
              </Button>
            </>
          ) : a.fix.action === 'compartments' ? (
            <span>
              Granting the missing {hidden && hidden.missing.length === 1 ? 'compartment' : 'compartments'} opens {items(a.fix.unlocks)}
              {inProject ? " (grant it to the project's members, and every member holds it here)" : ''}.{' '}
              {admin ? <>Grant {hidden && hidden.missing.length === 1 ? 'it' : 'them'} under {accessLink}.</> : 'Only an administrator grants a compartment; ask yours.'}
            </span>
          ) : a.fix.level ? (
            <span>
              A clearance of {LEVEL_TEXT[a.fix.level]} opens {items(a.fix.unlocks)}. {admin ? <>Raise it under {accessLink}.</> : "Only an administrator raises a person's clearance; ask yours."}
              {inProject ? " Or, if they are safe to share, label them lower under this project's Labelling Rules." : ''}
            </span>
          ) : null}
        </div>
      ) : null}
      <ErrorNote error={error} />
    </Explainer>
  );
}

/** Why the hidden items are hidden, in a sentence: above what the reader reads, in compartments they lack, or both. */
function hiddenWords(h: NonNullable<MemoryAccess['hidden']>, inProject: boolean): string {
  const parts: string[] = [];
  if (h.aboveLevel) parts.push(`${items(h.aboveLevel)} labelled above what you read${inProject && h.aboveProject ? ` (${h.aboveProject.toLocaleString()} of them above the project's classification)` : ''}`);
  if (h.compartments) parts.push(`${items(h.compartments)} in compartments you do not hold`);
  return `${items(h.total)} ${isAre(h.total)} hidden from you${parts.length ? `: ${parts.join(', and ')}` : ''}. `;
}

/**
 * The needs-you prompt on a project without a classification, for its owners: one choice. Until it is set the
 * project's memory counts as Internal; the Inbox carries the same ask.
 */
export function ClassifyPrompt({ projectId, onChange }: { projectId: string; onChange: () => void }) {
  const [error, setError] = useState<string>();
  const options = Object.fromEntries(CLASSIFICATION_ORDER.map((c) => [c, LEVEL_TEXT[c]])) as Record<Classification, string>;
  const set = async (to: Classification) => {
    try {
      setError(undefined);
      await routes.setProjectClassification(projectId, to);
      onChange();
    } catch (e) {
      setError(problemText(e));
    }
  };
  return (
    <Panel tone={VIEW_TONES.projects!} icon={<Shield />} title="Classify this project" hint="Needs you" actions={<ChoiceSelect label="Classification" value={undefined} placeholder="Choose a level" options={options} onChange={(v) => void set(v)} />}>
      <p className="m-0 text-sm text-muted">
        How sensitive is this project&apos;s data: Public, Internal, Confidential or Restricted? Members read its memory up to its classification, and it decides which AI providers its work may use. Until you choose, its memory counts as Internal, so members do not see anything labelled Confidential or Restricted.
      </p>
      <ErrorNote error={error} />
    </Panel>
  );
}
